import { useMutation } from '@apollo/client'
import { Box, FormModal, Link, Text } from '@chaine/keychaine'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'

import { Toast } from '../../../_shared/components/toast'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_TURVO_APP } from '../data/mutations'

/**
 * Saves Turvo app's username, database and password.
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const TurvoModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props

  const [formError, setFormError] = useState('')

  const { showToast } = Toast()

  const [enableTurvoApp, { loading: enableAppLoading }] = useMutation<{
    enableTurvoAppCredentials: { appType: string; databaseName: string; password: string; username: string }
  }>(ENABLE_TURVO_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: `Turvo app is enabled successfully!`
      })
      setFormError('')
      refetchApps()
      closeModal()
    },
    onError: (err) => {
      if (err.message === 'Incorrect login credentials') {
        setFormError('Please enter correct user name or password.')
        showToast({
          status: 'error',
          title: `Please enter correct user name or password`
        })
      } else if (err.message === 'Incorrect database name') {
        setFormError('Please enter correct database name.')
        showToast({
          status: 'error',
          title: `Please enter correct database name`
        })
      } else {
        setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
        showToast({
          status: 'error',
          title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        })
      }
    }
  })

  type FieldValues = {
    appApiKey?: string
    appClientID?: string
    appClientSecret?: string
    appPassword?: string
    appUsername?: string
  }
  const handleSaveDetails: SubmitHandler<FieldValues> = (event) => {
    const { appUsername, appPassword, appApiKey, appClientID, appClientSecret } = event
    enableTurvoApp({
      variables: {
        apiKey: appApiKey,
        appType: appType,
        clientID: appClientID,
        clientSecret: appClientSecret,
        password: appPassword,
        username: appUsername
      }
    })
  }

  const closeModal = () => {
    setFormError('')
    onClose()
  }

  const footer = (
    <Box>
      <Text color="red.600">{formError}</Text>
      <Text>
        {`See `}
        <Link
          color="blue.600"
          href="https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/turvo/"
          target="_blank"
          rel="noopener noreferrer"
        >
          detailed steps here.
        </Link>
      </Text>
    </Box>
  )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={closeModal}
        title={`Enable ${appType} for Chaine`}
        subtitle={`Enabling this integration requires an Admin Turvo account. Enter your admin username, password, API Key, Client ID and Client Secret.`}
        formSubmitHandler={handleSaveDetails}
        buttonText={'Enable'}
        initialValues={{}}
        validations={validations}
        isLoading={enableAppLoading}
        footer={formError ? footer : <></>}
        modalItems={[
          {
            labelText: 'Turvo Client ID',
            name: 'appClientID',

            placeHolder: 'Enter the Turvo Client ID',
            type: 'input'
          },
          {
            labelText: 'Turvo Client Secret',
            name: 'appClientSecret',

            placeHolder: 'Enter the Turvo Client Secret',
            type: 'input'
          },

          {
            labelText: 'Turvo API Key',
            name: 'appApiKey',

            placeHolder: 'Enter the Turvo API Key',
            type: 'input'
          },
          {
            labelText: 'Turvo Username',
            name: 'appUsername',
            placeHolder: 'Enter the Turvo Username',
            type: 'input'
          },
          {
            labelText: 'Turvo Password',
            name: 'appPassword',
            placeHolder: 'Enter the Turvo Password',
            type: 'input'
          }
        ]}
      />
    </>
  )
})

TurvoModal.displayName = 'TurvoModal'

export { TurvoModal }
